import React from "react";
import _ from "lodash";
import { Photo, Cart } from "../types";
import Modal from "../../../../utils/Modal";
import LightBox from "../../../BrowseEvent/components/albums/LightBox";
import Gallery from "react-grid-gallery";
import "../../styles/styles.css";
import { OrgDownloadContext } from "../../../OrganizationPhotos/OrgDownloadContext";
import { OrgDownloadContextType } from "../../../OrganizationPhotos/types";
import MediaThumbnailComponent from "./MediaThumbnailComponent";

interface PhotoGalleryProps {
  photos: Photo[];
  addPhotoToCart: (photo: Photo) => void;
  removePhotoFromCart: (photo: Photo) => void;
  cart: Cart;
  isEmbed: boolean;
  loadMorePhotos: () => void;
  isAuthorizedForDirectDownloads: boolean;
  primaryColor?: string;
  userId?: number;
  parentName?: string;
  securedOrgView?: boolean;
  registerGuestUserAPI: string;
  setGuestUserId: (userId: number) => void;
  secureAccessToken: string;
  disableSelectIcon?: boolean;
  directDownloadEnabled?: boolean;
}

interface PhotoGalleryState {
  showLightBox: boolean;
  photoIndex: number;
  photos: Photo[];
}

const tagStyle = {
  display: "inline",
  padding: ".2em .6em .3em",
  fontSize: "60%",
  fontWeight: "600",
  lineHeight: "1",
  color: "#FFF",
  background: "rgba(7,36,22,.6)",
  textAlign: "center",
  whiteSpace: "nowrap",
  verticalAlign: "baseline",
  borderRadius: ".25em"
};

const customThumbViewportStyle = () => ({ overflow: "hidden" });
const customThumbStyle = () => ({
  cursor: "pointer",
  marginTop: 0,
  WebkitTouchCallout: "none"
});

class PhotoGallery extends React.Component<
  PhotoGalleryProps,
  PhotoGalleryState,
  OrgDownloadContextType
> {
  static contextType = OrgDownloadContext;
  private prevDownloadableContent?: number[];

  constructor(props: PhotoGalleryProps) {
    super(props);
    this.state = {
      showLightBox: false,
      photoIndex: -1,
      photos: []
    };
  }

  public componentDidMount() {
    this.updatePhotos(
      this.props.photos,
      this.props.cart ? this.props.cart.photos : []
    );
  }

  public componentWillReceiveProps(nextProps: PhotoGalleryProps) {
    this.updatePhotos(
      nextProps.photos,
      nextProps.cart ? nextProps.cart.photos : []
    );
  }

  public componentDidUpdate(
    prevProps: PhotoGalleryProps,
    prevState: PhotoGalleryState
  ) {
    // Check if the context downloadableContent has changed
    if (this.context?.fromSecuredFaceSearch) {
      const { downloadableContent } = this.context;
      if (
        downloadableContent &&
        !_.isEqual(downloadableContent, this.prevDownloadableContent)
      ) {
        this.prevDownloadableContent = downloadableContent;
        this.updatePhotos(this.props.photos, this.props.cart?.photos || []);
      }
    }
  }

  handleImageSelect = ({ photo }: { photo: any }) => {
    this.updatePhotoInCart(photo);
  };

  public render() {
    return (
      <>
        <Gallery
          images={this.state.photos}
          rowHeight={120}
          margin={5}
          enableLightbox={false}
          enableImageSelection={false}
          thumbnailImageComponent={(props: any) => {
            return (
              <MediaThumbnailComponent
                props={props}
                handleImageSelect={this.handleImageSelect}
                primaryColor={this.props.primaryColor || "#34b2a7"}
                securedOrgView={this.props.securedOrgView}
                disableSelectIcon={this.props.disableSelectIcon}
                directDownloadEnabled={this.props.directDownloadEnabled}
              />
            );
          }}
          onClickThumbnail={this.showLightBox}
          tagStyle={tagStyle}
          selectedIconColor={this.props.primaryColor || "#34b2a7"}
          enableKeyboardInput={true}
          onContextMenu={this.contextMenu}
          tileViewportStyle={customThumbViewportStyle}
          thumbnailStyle={customThumbStyle}
          onRender={() => null}
        />

        {this.state.showLightBox ? (
          <Modal>
            <LightBox
              //@ts-ignore
              event={this.photoEvent()}
              eventClickable={true}
              //@ts-ignore
              album={this.photoAlbum()}
              photos={this.state.photos}
              photoIndex={this.state.photoIndex}
              closeLightBox={this.closeLightBox}
              showLightBox={this.showLightBox}
              cartHasPhotos={
                this.props.cart && this.props.cart.photos.length > 0
              }
              addToCart={this.props.addPhotoToCart}
              removeFromCart={this.props.removePhotoFromCart}
              cartId={this.props.cart ? this.props.cart.id : null}
              isEmbed={this.props.isEmbed}
              isAuthorizedForDirectDownloads={
                this.props.isAuthorizedForDirectDownloads
              }
              primaryColor={this.props.primaryColor || "#259fa5"}
              userId={this.props.userId}
              parentName={this.props.parentName}
              registerGuestUserAPI={this.props.registerGuestUserAPI}
              securedGalleryView={this.props.securedOrgView}
              secureAccessToken={this.props.secureAccessToken}
              setGuestUserId={this.props.setGuestUserId}
              directDownloadEnabled={this.props.directDownloadEnabled}
            />
          </Modal>
        ) : null}
      </>
    );
  }

  private updatePhotos = (photos: Photo[], photosInCart: number[]) => {
    const downloadableContent = this.context?.downloadableContent;

    this.setState({
      photos: photos.map((photo: Photo) => {
        const isSelected =
          downloadableContent && this.props.directDownloadEnabled
            ? downloadableContent.includes(photo.id)
            : _.includes(photosInCart, photo.id);

        return {
          ...photo,
          ...(photo.timeTaken
            ? {
                tags: [
                  {
                    title: "Time",
                    value: photo.timeTaken
                  }
                ]
              }
            : {}),
          isSelected,
          src: "",
          disableContextMenu: true
        };
      })
    });
  };

  private contextMenu = (e: any) => {
    e.preventDefault();
    return false;
  };

  private photoEvent = () =>
    this.state.photos[this.state.photoIndex].album.event;
  private photoAlbum = () => this.state.photos[this.state.photoIndex].album;

  private updatePhotoInCart = (photo: Photo) => {
    const {
      addContentToDownload,
      removeContentFromDownload,
      downloadableContent
    } = this.context || {};

    const isPhotoInDownloadList =
      downloadableContent?.includes(photo.id) ?? false;

    if (
      addContentToDownload &&
      removeContentFromDownload &&
      this.props.directDownloadEnabled
    ) {
      if (isPhotoInDownloadList) {
        removeContentFromDownload(photo);
      } else {
        addContentToDownload(photo);
      }
    } else {
      if (photo.isSelected) {
        this.props.removePhotoFromCart(photo);
      } else {
        this.props.addPhotoToCart(photo);
      }
    }
  };

  private showLightBox = (index: number) => {
    this.setState({ showLightBox: false }, () => {
      this.setState({
        photoIndex: index,
        showLightBox: true
      });
    });

    if (this.state.photos[index + 1] === undefined) {
      this.props.loadMorePhotos();
    }
  };

  private closeLightBox = () => {
    this.setState({
      showLightBox: false
    });
  };
}

export default PhotoGallery;
